import * as React from 'react';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import Image from 'next/image';
import {
  Box,
  Button,
  Flex,
  InputPassword,
  LinkButton,
  SmallAwningLogo,
  Text,
} from '@awning/components';
import { BaseLink } from '@/src/BaseLink';
import withSession from '@/src/auth/withSession';
import { FieldController } from '@/src/FieldController';
import { AuthServerSidePropsContext, TBasePageProps, TGetServerSideProps } from 'types';
import { loginSchema } from '@/src/Accounts';
import { useValidationResolver } from '@/src/useValidationResolver';
import { publicUrl } from '@/src/publicUrl';

type Props = TBasePageProps;

export default function Login() {
  const [status, setStatus] = React.useState<{ error: boolean | null; isLoading: boolean }>({
    error: null,
    isLoading: false,
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
  } = useForm({
    mode: 'all',
    reValidateMode: 'onBlur',
    resolver: useValidationResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  async function onSubmit({ email, password }: any) {
    setStatus({
      error: null,
      isLoading: true,
    });
    try {
      let path = null;
      if (window?.sessionStorage) {
        path = window.sessionStorage.getItem('redirectPath');
        window.sessionStorage.removeItem('redirectPath');
      }

      const {
        data: { redirectPath },
      }: any = await axios.post(publicUrl(`api/accounts/login`), {
        email,
        password,
        redirectPath: path,
      });

      // @ts-expect-error
      const url = new URL(window.location);
      url.pathname = redirectPath;
      window.location.href = url.href;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.log(error);
      setStatus({
        error: true,
        isLoading: false,
      });
    }
  }

  return (
    <Flex
      sx={{
        justifyContent: 'center',
        height: '100%',
      }}
    >
      <Flex
        sx={{
          height: 'auto',
          position: 'relative',
          width: { base: '0%', md: '50%' },
          visbility: { base: 'hidden', md: 'visible' },
        }}
      >
        <Box sx={{ display: { base: 'none', md: 'block' } }}>
          <Image
            src={publicUrl(`login.png`)}
            loading="eager"
            layout="fill"
            priority={true}
            objectFit="cover"
          />
        </Box>
      </Flex>

      <Flex
        sx={{
          alignItems: 'center',
          justifyContent: { base: 'flex-start', md: 'center' },
          flexDirection: 'column',
          width: { base: '100%', md: '50%' },
          padding: 8,
        }}
      >
        <Box
          as={SmallAwningLogo}
          textColor="black"
          width="36px"
          height="36px"
          sx={{ marginBottom: { base: 2, md: 5 } }}
        />
        <Box sx={{ maxWidth: { md: '500px' } }}>
          <Text
            sx={{
              display: 'block',
              text: { base: '2xl', md: '3xl' },
              fontWeight: 'bold',
              paddingY: 6,
            }}
          >
            Sign in
          </Text>

          <form onSubmit={handleSubmit(onSubmit)}>
            <FieldController
              control={control}
              label="Email"
              name="email"
              type="email"
              rules={{ required: true }}
              placeholder="Email"
              hideLabel
              sx={{ marginBottom: 5 }}
            />
            <FieldController
              control={control}
              label="Password"
              name="password"
              type="password"
              as={InputPassword}
              rules={{ required: true }}
              placeholder="Password"
              hideLabel
              sx={{ marginBottom: 5 }}
            />
            <Box>
              <Button
                type="submit"
                sx={{ marginY: 6, width: '100%' }}
                isDisabled={!isValid || !isDirty || status.isLoading}
              >
                Sign in
              </Button>
              {status.error && (
                <Box sx={{ text: 'xs', color: 'red.600', mt: 2 }}>
                  Incorrect email or password. Please try again.
                </Box>
              )}
            </Box>
          </form>

          <LinkButton
            variant="secondary"
            href={publicUrl(`accounts/forgot`)}
            sx={{ width: '100%' }}
          >
            Forgot password?
          </LinkButton>

          <Text
            sx={{
              display: 'block',
              color: 'gray.500',
              marginTop: 8,
            }}
          >
            Don't have an account?{'  '}
            <BaseLink href="/sign-up" sx={{ color: 'green.600' }}>
              Sign up
            </BaseLink>
          </Text>
        </Box>
      </Flex>
    </Flex>
  );
}

export const getServerSideProps = withSession(
  async ({ req }: AuthServerSidePropsContext): TGetServerSideProps<Props> => {
    const user = req.session.user;

    if (user !== undefined) {
      return { redirect: { destination: '/top-airbnb-markets', permanent: false } };
    }

    return {
      props: {},
    };
  }
);
